import { createSelector } from 'reselect';

import { CONTENT_FLOWS, MAX_VISIBLE_COLUMNS, SIDEBAR_FLOWS, VISIBLE_ROWS } from './_consts';

import { containedInRange, containedInRanges, rangesToStrings } from './_utils';

import { getTargetLanguagesHubDb } from 'factories/getTargetLanguagesHubDb';
import { getMostUsedSourceLanguagesUtil } from 'utils/getMostUsedSourceLanguagesUtil';
import { getMostUsedTargetLanguagesUtil } from 'utils/getMostUsedTargetLanguagesUtil';

export const getViewColumn = (state) => state.table.viewCoords.column;
export const getViewRow = (state) => state.table.viewCoords.row;
export const getViewCoords = (state) => state.table.viewCoords;
export const getSelectedRange = (state) => state.table.selectedRange;
export const getExcludedRanges = (state) => state.table.excludedRanges;
export const getSelectedCell = (state) => state.table.selectedCell;
export const getLanguagePairs = (state) => state.staticData.languagePairs || [];
export const getQualities = (state) => state.staticData.qualities || [];
export const getContentFlow = (state) => state.contentFlow;
export const getInputs = (state) => state.inputs;
export const getColumnMapping = (state) => state.columnMapping;
export const getGlossaries = (state) => state.glossaries;
export const getSidebarFlow = (state) => state.sidebarFlow;
export const getFiles = (state) => state.fileData;
export const getFilteredContent = (state) => state.filteredContent;
export const getFileCount = (state) => state.fileData?.length;
export const getSelectedFileIndex = (state) => state.selectedFileIndex || 0;

// File selectors
// ============================================================
export const getSelectedFile = createSelector(
  [getFiles, getSelectedFileIndex],
  (files, selectedFileIndex) => files[selectedFileIndex] || {}
);

export const getTotalColumns = createSelector(
  [getSelectedFile],
  (selectedFile) => selectedFile.totalColumns
);

export const getTotalRows = createSelector(
  [getSelectedFile],
  (selectedFile) => selectedFile.totalRows
);

export const getKeys = createSelector([getSelectedFile], (selectedFile) => selectedFile.keys);

export const getFileId = createSelector([getSelectedFile], (selectedFile) => selectedFile.id);

export const getFileIds = createSelector([getFiles], (files) => files.map((f) => f.id));

export const getXliffBased = createSelector(
  [getSelectedFile],
  (selectedFile) => selectedFile.xliffBased
);

export const getSupportsPlainText = createSelector(
  [getSelectedFile],
  (selectedFile) => selectedFile.supportsPlainText
);

export const getContentType = createSelector(
  [getSelectedFile],
  (selectedFile) => selectedFile.contentType
);

// =============================================================

export const getShouldPreventEditing = createSelector([getSidebarFlow], (sidebarFlow) => {
  const { ORDER_DETAILS, PAYMENT_DETAILS } = SIDEBAR_FLOWS;

  return [ORDER_DETAILS, PAYMENT_DETAILS].includes(sidebarFlow);
});

// AJAX params selectors
// ============================================================
export const getCanBeCalculated = createSelector(
  [getContentFlow, getInputs, getColumnMapping],
  (contentFlow, inputs, columnMapping) => {
    const { TABLE, TEXT_FILE, TEXT } = CONTENT_FLOWS,
      { copy, sourceLanguageCode, targetLanguageCodes } = inputs;

    if (!sourceLanguageCode) return false;

    if (targetLanguageCodes?.length === 0) {
      const validationColumnMapping = columnMapping.reduce(
        (acc, cm) =>
          acc &&
          !!cm?.sourceColumn &&
          cm?.targetsColumn?.every((tc) => !!tc.column && !!tc.language),
        true
      );
      if (validationColumnMapping) {
        const targetLanguagesHubDb = getTargetLanguagesHubDb(columnMapping);
        if (!targetLanguagesHubDb?.length) return false;
      } else {
        return false;
      }
    }

    if (![TABLE, TEXT_FILE, TEXT].includes(contentFlow)) return false;
    if (contentFlow === TEXT && !copy?.length) return false;
    return true;
  }
);

export const getIsAFile = createSelector([getContentFlow], (contentFlow) => {
  return contentFlow == CONTENT_FLOWS.TEXT_FILE || contentFlow == CONTENT_FLOWS.TABLE;
});

export const getIsPlainTextConfigurable = createSelector(
  [getIsAFile, getSupportsPlainText],
  (isAFile, supportsPlainText) => {
    if (!isAFile) return true;
    return supportsPlainText;
  }
);

export const getWordCountParams = createSelector(
  [getCanBeCalculated, getIsAFile, getInputs, getFileIds, getExcludedRanges],
  (canBeCalculated, isAFile, inputs, fileIds, excluded) => {
    if (!canBeCalculated) return;

    const { copy, sourceLanguageCode } = inputs;

    if (isAFile)
      return {
        sourceLanguageCode,
        fileIds,
        excluded: rangesToStrings(excluded)
      };

    return { copy };
  }
);

export const getOrderDetailsParams = createSelector(
  [getCanBeCalculated, getIsAFile, getInputs, getFileIds, getExcludedRanges],
  (canBeCalculated, isAFile, inputs, fileIds, excluded) => {
    if (!canBeCalculated) return;

    const { sourceLanguageCode, targetLanguageCodes, quality, copy } = inputs,
      common = { sourceLanguageCode, targetLanguageCodes, quality };

    if (isAFile)
      return {
        ...common,
        fileIds,
        excluded: rangesToStrings(excluded)
      };

    return { ...common, copy };
  }
);

export const getCharLimit = createSelector([getInputs], (inputs) => {
  const { charLimit } = inputs;

  return charLimit;
});

export const getIntegration = createSelector([getInputs], (inputs) => inputs?.integration);

export const getCreateTrParams = createSelector(
  [getCanBeCalculated, getInputs, getGlossaries, getIsAFile, getFileIds, getExcludedRanges],
  (canBeCalculated, inputs, glossaries, isAFile, uploadedFileIds, excluded) => {
    const { copy, charLimit, selectedEditors, htmlAllowed, ...commonInputs } = inputs,
      editorPerLanguage = selectedEditors?.perLanguage;

    const generalEditorId =
      editorPerLanguage || !selectedEditors?.generalEditor.length
        ? null
        : selectedEditors.generalEditor;
    const languageEditors = selectedEditors?.languageEditors ?? [];

    const editorIdsPerLanguage = editorPerLanguage
      ? Object.keys(languageEditors)?.reduce((acc, languageCode) => {
          if (languageEditors[languageCode]) acc[languageCode] = languageEditors[languageCode];

          return acc;
        }, {})
      : null;

    const commonInputs2 = {
      editorIdsPerLanguage,
      editorPerLanguage,
      generalEditorId,
      plainText: !htmlAllowed,
      assignedGlossariesIds: glossaries
    };

    if (!canBeCalculated) return;

    if (isAFile)
      return {
        ...commonInputs,
        ...commonInputs2,
        excluded: rangesToStrings(excluded),
        uploadedFileIds
      };

    return {
      ...commonInputs,
      ...commonInputs2,
      charLimit,
      copy
    };
  }
);

// Other selectors
// ============================================================
export const getMostUsedSourceLanguages = createSelector(
  [getLanguagePairs],
  getMostUsedSourceLanguagesUtil
);

export const getMostUsedTargetLanguages = createSelector(
  [getLanguagePairs],
  getMostUsedTargetLanguagesUtil
);

export const getOrderableQualities = createSelector(
  [getQualities, getXliffBased],
  (qualities, isXliff) => {
    if (!isXliff) return qualities;

    return qualities.filter((q) => q != 'machine');
  }
);

// Table navigation selectors
// ============================================================
export const getVisibleColumns = createSelector([getTotalColumns], (totalColumns) =>
  totalColumns > MAX_VISIBLE_COLUMNS ? MAX_VISIBLE_COLUMNS : totalColumns
);

export const getColumns = createSelector(
  [getViewColumn, getVisibleColumns],
  (viewColumn, visibleColumns) =>
    [...Array(visibleColumns).keys()].map((index) => index + viewColumn)
);

export const getRows = createSelector([getViewRow], (viewRow) =>
  [...Array(VISIBLE_ROWS).keys()].map((index) => index + viewRow)
);

export const getFirstColumnCoord = createSelector([getViewRow], (row) => ({ column: 1, row }));

export const getPreviousColumnCoord = createSelector(
  [getViewColumn, getViewRow, getVisibleColumns],
  (viewColumn, row, visibleColumns) => ({
    column: viewColumn - visibleColumns,
    row
  })
);

export const getNextColumnCoord = createSelector(
  [getViewColumn, getViewRow, getVisibleColumns],
  (viewColumn, row, visibleColumns) => ({
    column: viewColumn + visibleColumns,
    row
  })
);

export const getLastColumnCoord = createSelector(
  [getViewRow, getVisibleColumns, getTotalColumns],
  (row, visibleColumns, totalColumns) => {
    const rest = totalColumns % visibleColumns;

    if (rest === 0) return { column: totalColumns - visibleColumns + 1, row };

    return { column: totalColumns - rest + 1, row };
  }
);

export const getFirstRowCoord = createSelector([getViewColumn], (column) => ({ column, row: 1 }));

export const getPreviousRowCoord = createSelector(
  [getViewColumn, getViewRow],
  (column, viewRow) => ({
    column,
    row: viewRow - VISIBLE_ROWS
  })
);

export const getNextRowCoord = createSelector([getViewColumn, getViewRow], (column, viewRow) => ({
  column,
  row: viewRow + VISIBLE_ROWS
}));

export const getLastRowCoord = createSelector(
  [getViewColumn, getTotalRows],
  (column, totalRows) => ({
    column,
    row: totalRows - (totalRows % VISIBLE_ROWS) + 1
  })
);

export const getRowPages = createSelector(
  [getTotalRows],
  (totalRows) => (totalRows - (totalRows % VISIBLE_ROWS)) / VISIBLE_ROWS + 1
);

export const getRowPage = createSelector(
  [getViewRow],
  (row) => (row - (row % VISIBLE_ROWS)) / VISIBLE_ROWS + 1
);

export const getColumnPages = createSelector(
  [getTotalColumns, getVisibleColumns],
  (totalColumns, visibleColumns) => {
    const rest = totalColumns % visibleColumns;

    if (rest === 0) return totalColumns / visibleColumns;

    return (totalColumns - rest) / visibleColumns + 1;
  }
);

export const getColumnPage = createSelector(
  [getViewColumn, getVisibleColumns],
  (column, visibleColumns) => (column - (column % visibleColumns)) / visibleColumns + 1
);

// Table range selection selectors
// ============================================================
export const getHasSelectedCell = createSelector(
  [getSelectedCell],
  (selectedCell) => selectedCell.column !== undefined
);

export const getIsSelectedCellInSelectedRange = createSelector(
  [getHasSelectedCell, getSelectedCell, getSelectedRange],
  (hasSelectedCell, selectedCell, selectedRange) => {
    if (!hasSelectedCell) return false;
    return containedInRange(selectedCell, selectedRange);
  }
);

export const getSelectedExcludedRanges = createSelector(
  [getHasSelectedCell, getSelectedCell, getExcludedRanges],
  (hasSelectedCell, selectedCell, excludedRanges) => {
    if (!hasSelectedCell) return [];
    return excludedRanges
      .map((range, originalIndex) => ({ ...range, originalIndex }))
      .filter((range) => containedInRange(selectedCell, range));
  }
);

export const getIsSelectingRange = createSelector(
  [getHasSelectedCell, getSelectedExcludedRanges, getSelectedRange],
  (hasSelectedCell, selectedExcludedRanges, selectedRanges) => {
    if (!hasSelectedCell) return false;
    if (!selectedRanges.start) return false;
    if (selectedRanges.end) return false;
    if (selectedExcludedRanges.length > 0) return false;
    return true;
  }
);

export const getHasSelectedCellInExcludedRanges = createSelector(
  [getSelectedExcludedRanges],
  (selectedExcludedRanges) => selectedExcludedRanges.length > 0
);

export const getShouldShowPopup = createSelector(
  [getIsSelectedCellInSelectedRange, getHasSelectedCellInExcludedRanges],
  (isSelectedCellInSelectedRange, hasSelectedCellInExcludedRanges) =>
    isSelectedCellInSelectedRange || hasSelectedCellInExcludedRanges
);

export const getSelectedCellSurrondings = createSelector(
  [getHasSelectedCell, getSelectedCell],
  (hasSelectedCell, { row, column }) => {
    if (!hasSelectedCell) return {};
    return {
      top: { column, row: row - 1 },
      bottom: { column, row: row + 1 },
      right: { column: column + 1, row }
    };
  }
);

export const getSelectedRangeAvailablePopupPositions = createSelector(
  [getIsSelectedCellInSelectedRange, getSelectedCellSurrondings, getSelectedRange],
  (isSelectedCellInSelectedRange, { top, bottom, right }, selectedRange) => {
    let positions = [];

    if (!isSelectedCellInSelectedRange) return [];
    if (!containedInRange(top, selectedRange)) positions.push('top');
    if (!containedInRange(bottom, selectedRange)) positions.push('bottom');
    if (!containedInRange(right, selectedRange)) positions.push('right');

    return positions;
  }
);

export const getExcludedRangesAvailablePopupPositions = createSelector(
  [getHasSelectedCellInExcludedRanges, getSelectedCellSurrondings, getExcludedRanges],
  (hasSelectedCellInExcludedRanges, { top, bottom, right }, excludedRanges) => {
    let positions = [];

    if (!hasSelectedCellInExcludedRanges) return [];
    if (!containedInRanges(top, excludedRanges)) positions.push('top');
    if (!containedInRanges(bottom, excludedRanges)) positions.push('bottom');
    if (!containedInRanges(right, excludedRanges)) positions.push('right');

    return positions;
  }
);

export const getAvailablePopupPositions = createSelector(
  [
    getIsSelectedCellInSelectedRange,
    getHasSelectedCellInExcludedRanges,
    getSelectedRangeAvailablePopupPositions,
    getExcludedRangesAvailablePopupPositions
  ],
  (inSelectedRange, inExcludedRanges, availableInSelectedRange, availableInExcludedRanges) => {
    if (inSelectedRange) return availableInSelectedRange;
    if (inExcludedRanges) return availableInExcludedRanges;
    return [];
  }
);
